// frontend_react/src/context/LoginContext.jsx

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import {
  fetchCurrentUser,
  login as apiLogin,
  logout as apiLogout,
} from "../services/authApi";

import logger from "../utils/logger";

export const LoginContext = createContext(null);

export const LoginProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const normalizeUser = useCallback((data) => {
    if (!data) return null;

    return {
      ...data,

      // Source de vérité frontend multi-company
      uuid: data.uuid || null,
      email: data.email || null,
      role: data.role || null,
      company_uuid: data.company_uuid || null,
      company_name: data.company_name || data.company?.name || null,

      authenticated: true,
    };
  }, []);

  const clearAuthStorage = useCallback(() => {
    localStorage.removeItem("csrf_access_token");
    localStorage.removeItem("csrf_refresh_token");
  }, []);

  const fetchUser = useCallback(async () => {
    setAuthLoading(true);

    try {
      const { data } = await fetchCurrentUser();
      setUser(normalizeUser(data));
      return normalizeUser(data);
    } catch (err) {
      logger.warn("⛔️ fetchUser failed", err);
      setUser(null);
      return null;
    } finally {
      setAuthLoading(false);
    }
  }, [normalizeUser]);

  const login = useCallback(
    async ({ email, password }) => {
      setLoading(true);
      setError("");
      setSuccess("");

      try {
        await apiLogin({ email, password });
        const connectedUser = await fetchUser();

        setSuccess("Connexion réussie");

        if (connectedUser) {
          navigate("/cart", { replace: true });
        }

        return connectedUser;
      } catch (err) {
        const message =
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Échec de la connexion";

        setError(message);
        setUser(null);

        return null;
      } finally {
        setLoading(false);
      }
    },
    [fetchUser, navigate]
  );

  const logout = useCallback(async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await apiLogout();
    } catch (err) {
      logger.error("Erreur logout backend :", err);
    } finally {
      clearAuthStorage();

      setUser(null);
      setAuthLoading(false);
      setLoading(false);

      navigate("/", { replace: true });
    }
  }, [clearAuthStorage, navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const value = useMemo(
    () => ({
      // Compatibilité existante
      user,

      // Alias recommandé pour la suite du chantier
      currentUser: user,

      isAuthenticated: !!user,
      authLoading,
      loading,
      error,
      success,

      login,
      logout,
      fetchUser,

      setError,
      setSuccess,

      // Helpers pratiques
      isSuperAdmin: user?.role === "super_admin" || user?.role === "ROLE_SUPER_ADMIN",
      isAdmin: user?.role === "admin" || user?.role === "ROLE_ADMIN",
      companyUuid: user?.company_uuid || null,
      companyName: user?.company_name || null,
    }),
    [user, authLoading, loading, error, success, login, logout, fetchUser]
  );

  return <LoginContext.Provider value={value}>{children}</LoginContext.Provider>;
};

LoginProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useLogin = () => {
  const context = useContext(LoginContext);

  if (!context) {
    throw new Error("useLogin must be used within a LoginProvider");
  }

  return context;
};